import { create } from 'zustand';
import type { DiceResult } from '../components/DiceRoller';
import { useAudioStore } from './audioStore';

type RollFunction = (notation: string) => Promise<DiceResult>;

interface DiceStore {
  rollFn: RollFunction | null;
  isReady: boolean;
  lastResult: DiceResult | null;
  setRollFn: (fn: RollFunction) => void;
  roll: (notation: string) => Promise<DiceResult | null>;
  setLastResult: (result: DiceResult) => void;
}

// Module-level lock — prevents re-entrant / concurrent rolls.
// Synchronous boolean check is atomic in single-threaded JS.
let _rolling = false;

export const useDiceStore = create<DiceStore>((set, get) => ({
  rollFn: null,
  isReady: false,
  lastResult: null,

  setRollFn: (fn) => set({ rollFn: fn, isReady: true }),

  roll: async (notation) => {
    const { rollFn } = get();
    if (!rollFn) {
      console.warn('Dice roller not ready');
      return null;
    }

    // Hard lock: drop concurrent calls while a roll is in progress.
    // Sequential awaits (advantage/disadvantage) still work because
    // the lock clears before the next `await roll()` resumes.
    if (_rolling) {
      console.warn('[DiceStore] Roll already in progress, dropping');
      return null;
    }
    _rolling = true;

    try {
      // Trigger dice roll sound
      useAudioStore.getState().playDiceSound();

      const result = await rollFn(notation);
      set({ lastResult: result });
      return result;
    } catch (err) {
      console.error('[DiceStore] Roll failed:', err);
      return null;
    } finally {
      _rolling = false;
    }
  },

  setLastResult: (result) => set({ lastResult: result }),
}));
